import * as React from 'react';
import { useRef } from 'react';
import { ChevronLeft, Loader2, CheckCircle2, Download, TriangleAlert, X } from 'lucide-react';
import { Project } from '@shared/types/tsk';
import { ReportExportTemplate } from './ReportExportTemplate';
import BillingWall from './BillingWall';
import { GeminiPage1Content } from './GeminiPage1Content';
import { GeminiPage2Content } from './GeminiPage2Content';
import { useGeminiPanel } from './useGeminiPanel';

interface GeminiPanelProps {
  project: Project | undefined;
  onClose: () => void;
  autoDownload?: boolean;
}

const GeminiPanel = ({ project, onClose, autoDownload = false }: GeminiPanelProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const exportTemplateRef = useRef<HTMLDivElement>(null);

  const {
    status, isDownloading, showSuccess, setShowSuccess, aiStatement,
    flattenedPlan, isFlattening, previewScale, isAuthorized, setIsAuthorized,
    showBilling, setShowBilling, isCheckingAuth, downloadError, setDownloadError,
    fixtureCounts, fittingCounts, handlePrint
  } = useGeminiPanel(project, onClose, autoDownload, containerRef, exportTemplateRef);

  const pageWidthPx = 210 * 3.7795;
  const pageHeightPx = 297 * 3.7795;

  return (
    <div className="flex flex-col h-full bg-[#0f172a] text-[#f1f5f9] overflow-hidden relative" data-testid="gemini-panel">
      {/* Loading Overlay */}
      {isDownloading && (
        <div className="absolute inset-0 z-[100] bg-[#020617]/80 backdrop-blur-lg flex flex-col items-center justify-center gap-6 text-white text-center p-10">
          <Loader2 className="animate-spin text-blue-500" size={64} />
          <div className="space-y-2">
            <h3 className="font-black text-xl uppercase tracking-widest">Generating PDF</h3>
            <p className="text-sm font-bold text-white/50 uppercase tracking-wider">Please don't close this window...</p>
          </div>
        </div>
      )}

      {/* Success Toast */}
      {showSuccess && (
        <div className="absolute top-24 left-1/2 -translate-x-1/2 z-[110] bg-white text-[#0f172a] px-6 py-4 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.5)] flex items-center gap-4 animate-in fade-in zoom-in duration-500 border-2 border-emerald-500">
          <div className="bg-emerald-500 text-white p-2 rounded-xl">
            <CheckCircle2 size={24} />
          </div>
          <div className="flex flex-col">
            <p className="text-sm font-black uppercase tracking-widest">Report Ready</p>
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">File saved successfully.</p>
          </div>
          <button onClick={() => setShowSuccess(false)} className="ml-4 text-slate-300 hover:text-slate-900 transition-colors">
            <X size={20} />
          </button>
        </div>
      )}

      {/* Error Toast */}
      {downloadError && (
        <div className="absolute top-24 left-1/2 -translate-x-1/2 z-[110] bg-white text-[#0f172a] px-6 py-4 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.5)] flex items-center gap-4 animate-in fade-in zoom-in duration-500 border-2 border-red-500">
          <div className="bg-red-500 text-white p-2 rounded-xl">
            <TriangleAlert size={24} />
          </div>
          <div className="flex flex-col">
            <p className="text-sm font-black uppercase tracking-widest text-red-600">Download Error</p>
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{downloadError}</p>
          </div>
          <button onClick={() => setDownloadError(null)} className="ml-4 text-slate-300 hover:text-slate-900 transition-colors">
            <X size={20} />
          </button>
        </div>
      )}

      {/* Header */}
      <header className="flex items-center justify-between px-6 py-4 glass-panel z-10 shrink-0">
        <div className="flex items-center gap-4">
          <button onClick={onClose} className="p-2 hover:bg-white/5 rounded-xl transition-colors text-[#94a3b8] hover:text-white">
            <ChevronLeft size={24} />
          </button>
          <div className="hidden sm:block">
            <h1 className="text-lg font-black tracking-tight text-white uppercase leading-none">Draft Submission</h1>
            <p className="text-[10px] font-bold text-white/30 uppercase tracking-widest mt-1">Verify technical details</p>
          </div>
        </div>
        <div className="flex gap-3">
          {status === 'done' && (
            <button
              onClick={handlePrint}
              disabled={isFlattening || isDownloading}
              data-testid="gemini-export-pdf-btn"
              className={`flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-black text-xs uppercase tracking-[0.2em] shadow-2xl shadow-blue-500/20 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed ring-1 ring-white/10`}
            >
              <Download size={18} />
              <span>{isFlattening ? 'Processing...' : 'Export PDF'}</span>
            </button>
          )}
        </div>
      </header>

      {/* Main Content Area */}
      <div
        ref={containerRef}
        className={`flex-1 overflow-y-auto bg-[#020617] p-4 sm:p-12 space-y-20 flex flex-col items-center no-scrollbar scroll-smooth transition-all duration-1000 ${(!isAuthorized && !isCheckingAuth) ? 'blur-2xl pointer-events-none' : ''}`}
      >
        {isCheckingAuth ? (
          <div className="flex-1 flex flex-col items-center justify-center space-y-6 animate-in fade-in duration-700">
             <Loader2 className="animate-spin text-blue-500" size={56} />
             <p className="font-black text-xs uppercase tracking-widest text-white/50">Verifying License...</p>
          </div>
        ) : (status === 'analyzing' || isFlattening) ? (
          <div className="flex-1 flex flex-col items-center justify-center space-y-6 animate-in fade-in duration-700">
            <Loader2 className="animate-spin text-blue-500" size={56} />
            <div className="text-center">
              <p className="font-black text-sm uppercase tracking-[0.3em] text-white">Assembling Report</p>
              <p className="text-[10px] font-bold text-white/30 uppercase tracking-widest mt-2">Checking standards & counts...</p>
            </div>
          </div>
        ) : (
          <div
            className="flex flex-col items-center gap-12"
            style={{ width: pageWidthPx * previewScale, paddingBottom: '100px' }}
          >
            {/* Page 1: Metadata */}
            <div
              className="origin-top"
              style={{ transform: `scale(${previewScale})`, height: pageHeightPx * previewScale, width: pageWidthPx }}
            >
              <div
                id="capture-page-1"
                data-testid="page1"
                className="bg-white text-[#0f172a] shadow-[0_30px_60px_rgba(0,0,0,0.5)] w-[210mm] h-[297mm] p-16 flex flex-col border border-white/10"
              >
                <GeminiPage1Content project={project} fixtureCounts={fixtureCounts} fittingCounts={fittingCounts} aiStatement={aiStatement} />
              </div>
            </div>

            {/* Page 2: Scaled Markup overlay */}
            <div
              className="origin-top"
              style={{ transform: `scale(${previewScale})`, height: pageHeightPx * previewScale, width: pageWidthPx }}
            >
                <div
                  id="capture-page-2"
                  className="bg-white text-[#0f172a] shadow-[0_30px_60px_rgba(0,0,0,0.5)] w-[210mm] h-[297mm] p-16 flex flex-col border border-white/10"
                >
                  <GeminiPage2Content project={project} markup={project?.markup || { lines: [], symbols: [] }} flattenedPlan={flattenedPlan} />
                </div>
            </div>
          </div>
        )}
      </div>

      <footer className="p-4 bg-[#0f172a] border-t border-white/5 text-center shrink-0">
        <p className="text-[9px] font-black text-white/20 uppercase tracking-[0.4em]">TradeSketch Pro • Compliance Report Utility</p>
      </footer>

      {/* Hidden export template for pdf-lib capture */}
      <div style={{ position: 'fixed', left: '-10000px', top: 0, pointerEvents: 'none' }}>
        <div ref={exportTemplateRef} style={{ width: '210mm', padding: 0, margin: 0, display: 'block', backgroundColor: 'white' }}>
          <ReportExportTemplate 
            project={project}
            fixtureCounts={fixtureCounts}
            fittingCounts={fittingCounts}
            aiStatement={aiStatement}
            flattenedPlan={flattenedPlan}
          />
        </div>
      </div>

      {/* Paywall */}
      {showBilling && project && (
        <BillingWall 
          projectId={project.id} 
          onClose={() => { setShowBilling(false); onClose(); }} 
          onSuccess={() => { setShowBilling(false); setIsAuthorized(true); }}
        />
      )}
    </div>
  );
};

export default GeminiPanel;
